import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { areArraysEquivalent } from '@emma-helpers/emma-utils';
import { noop } from 'lodash';

import { EMMAFormElementComponent } from './emma-form-element.component';

const EMMA_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAInputListComponent),
  multi: true,
};

const EMMA_INPUT_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAInputListComponent),
  multi: true,
};

@Component({
    selector: 'emma-input-list',
    templateUrl: './emma-input-list.component.html',
    providers: [
        EMMA_INPUT_CONTROL_VALUE_ACCESSOR,
        EMMA_INPUT_VALIDATOR,
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMAInputListComponent,
        },
    ],
    standalone: false
})
export class EMMAInputListComponent
  extends EMMAFormElementComponent
  implements OnInit, ControlValueAccessor, Validator
{
  // + input interface
  @Input() override autofocus = false;
  @Input() override placeholder = '';

  // Validation options
  @Input() minlength = 0;
  @Input() maxlength = Infinity;

  // Flags + params
  @Input() copyToClipboard = false;
  @Input() valueType: 'string' | 'array' = 'array';
  @Input() inputClass = 'form-control m-input';

  @Input() splitChars = ' ,;';
  @Input() joinChar = '; ';

  @Output() override $focus = new EventEmitter<any>();
  @Output() override $blur = new EventEmitter<string | Array<string>>();
  @Output() override $change = new EventEmitter<string | Array<string>>();
  @Output() override $keydown = new EventEmitter<any>();
  @Output() override $keyup = new EventEmitter<any>();
  @Output() override $keypress = new EventEmitter<any>();

  // Form
  @Input()
  get value(): string | Array<string> {
    return this.valueType === 'string' ? this.arrayToString(this._innerValue) : this._innerValue;
  }
  set value(value: string | Array<string>) {
    this.writeValue(value);
    const output = this.valueType === 'string' ? this.arrayToString(this._innerValue) : this._innerValue;
    this.onChangeCallback(output);
    this.$change.emit(output);
  }

  get strValue(): string {
    return this.arrayToString(this._innerValue);
  }

  private _innerValue: Array<string> = [];
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: string | Array<string>) => void = noop;

  // Select mode
  label = '';

  override ngOnInit(): void {
    this.updateClasses();
  }

  // Set touched on blur
  onBlur() {
    this.onTouchedCallback();
    this.$blur.emit(this.value);
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.$enter.emit(event);
    }
  }

  private stringToArray(str: string) {
    return str
      .replace(new RegExp(`[${this.splitChars}]+`, 'g'), this.joinChar.trim())
      .split(this.joinChar.trim())
      .map((v) => v.trim())
      .filter(Boolean);
  }

  private arrayToString(arr: Array<string>) {
    return arr.join(this.joinChar);
  }

  // From ControlValueAccessor interface
  writeValue(value: string | Array<string>) {
    const valueArray = Array.isArray(value) ? value : this.stringToArray(value || '');
    if (!areArraysEquivalent(valueArray, this._innerValue)) {
      this._innerValue = [...valueArray];
    }
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  // From Validator interface
  override validate: ValidatorFn = (control: AbstractControl) => {
    const validators: ValidatorFn[] = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    if (this.maxlength < Infinity) {
      validators.push(Validators.maxLength(this.maxlength));
    }
    if (this.minlength) {
      validators.push(Validators.minLength(this.minlength));
    }
    if (this.validateFn) {
      validators.push(this.validateFn);
    }
    if (validators.length) {
      const validator = Validators.compose(validators);
      return validator ? validator(control) : null;
    }
    return null;
  };
}
